import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useToast } from "@/components/ui/Toast";
import {
  socialLinks,
  offices,
  contactEmails,
  platformItems,
} from "@/lib/nav-data";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const socialIcon = {
  LinkedIn: Linkedin,
  X: Twitter,
  YouTube: Youtube,
};

export function SiteFooter() {
  const { t } = useT("footer");
  const { t: tNav } = useT("nav");
  const locale = useLocale();
  const L = (href) => addLocalePrefix(href, locale);

  return (
    <footer
      className="border-t border-hairline bg-surface-void"
      data-testid="site-footer"
    >
      <div className="container-frac py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
          {/* Brand + newsletter */}
          <div className="lg:pr-6">
            <Link
              to={L("/")}
              aria-label="Frac home"
              className="inline-block"
              data-testid="footer-logo-link"
            >
              <img
                src="/brand/frac-logo-white.png"
                alt="Frac"
                width={140}
                height={30}
                className="h-8 w-auto"
                data-testid="footer-logo-img"
              />
            </Link>
            <p className="mt-6 max-w-sm text-sm leading-normal text-ink-secondary">
              {t("tagline")}
            </p>
            <NewsletterForm />
          </div>

          {/* Platform */}
          <FooterCol title={t("columns.platform")}>
            {platformItems.map((p) => {
              const localisedLabel = tNav(`platformItems.${p.key}.label`, {
                defaultValue: p.label,
              });
              const cleanLabel = localisedLabel.replace(/^\d+\s*[·.]\s*/, "");
              return (
                <FooterLink
                  key={p.hrefBase}
                  to={L(p.hrefBase)}
                  testid={`footer-platform-${p.hrefBase}`}
                >
                  {cleanLabel}
                </FooterLink>
              );
            })}
            <FooterLink to={L("/intelligence")} testid="footer-platform-intelligence">
              {t("links.intelligenceTerminal")}
            </FooterLink>
          </FooterCol>

          {/* Company */}
          <FooterCol title={t("columns.company")}>
            <FooterLink to={L("/company")} testid="footer-company">
              {t("links.company")}
            </FooterLink>
            <FooterLink to={L("/solutions")} testid="footer-solutions">
              {t("links.solutions")}
            </FooterLink>
            <FooterLink to={L("/newsroom")} testid="footer-newsroom">
              {t("links.newsroom")}
            </FooterLink>
            <FooterLink to={L("/contact")} testid="footer-contact">
              {t("links.contact")}
            </FooterLink>
          </FooterCol>

          {/* Resources */}
          <FooterCol title={t("columns.resources")}>
            <FooterLink to={L("/verified-documents")} testid="footer-verified-documents">
              {t("links.verifiedDocuments")}
            </FooterLink>
            <li>
              <a
                href="mailto:media@frac.io"
                className="text-sm text-ink-secondary hover:text-ink-primary transition-colors duration-fast ease-out"
                data-testid="footer-press-kit"
              >
                {t("links.pressKit")}
              </a>
            </li>
            {/* Legal pages stay English only; link straight to /privacy /terms */}
            <FooterLink to="/privacy" testid="footer-privacy">
              {t("links.privacy")}
            </FooterLink>
            <FooterLink to="/terms" testid="footer-terms">
              {t("links.terms")}
            </FooterLink>
          </FooterCol>

          {/* Contact */}
          <FooterCol title={t("columns.contact")}>
            {contactEmails.map((c) => (
              <li key={c.email}>
                <a
                  href={`mailto:${c.email}`}
                  className="text-sm text-ink-secondary hover:text-ink-primary transition-colors duration-fast ease-out"
                  data-testid={`footer-email-${c.label.toLowerCase()}`}
                >
                  <span className="block text-xs uppercase tracking-caps text-ink-secondary">
                    {t(`emailLabels.${c.label}`, { defaultValue: c.label })}
                  </span>
                  {c.email}
                </a>
              </li>
            ))}
          </FooterCol>
        </div>

        {/* Offices */}
        <div className="mt-16 grid gap-8 border-t border-hairline pt-10 sm:grid-cols-2">
          {offices.map((o) => (
            <div key={o.city} data-testid={`footer-office-${o.city.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-xs font-semibold uppercase tracking-caps text-ink-secondary">
                {t(`cityLabels.${o.city}`, { defaultValue: o.city })}
              </div>
              <div className="mt-2 text-sm leading-normal text-ink-secondary">
                {o.address}
              </div>
            </div>
          ))}
        </div>

        {/* Legal / social */}
        <div className="mt-16 flex flex-col gap-6 border-t border-hairline pt-8 sm:flex-row sm:items-start sm:justify-between">
          {/* PENDING_LEGAL_REVIEW: Corporate-structure disclosure — pending legal review */}
          <p
            className="max-w-2xl text-xs leading-normal text-ink-secondary italic"
            data-testid="footer-corporate-structure"
          >
            {t("corporateStructure")}
          </p>

          <ul className="flex items-center gap-2" aria-label="Social links">
            {socialLinks.map((s) => {
              const Icon = socialIcon[s.label];
              return (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t("socialAria", { name: s.label })}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-hairline text-ink-secondary hover:text-frac-violet hover:border-active-border transition-colors duration-fast ease-out"
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                  >
                    {Icon && <Icon className="h-4 w-4" strokeWidth={1.5} />}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4 text-xs text-ink-secondary sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div
            className="flex flex-wrap items-center gap-4"
            aria-label="Recognition"
            data-testid="footer-recognition"
          >
            <a
              href="https://drive.google.com/file/d/1u6KArN1vFKPc5AJU6OSha_jx91TMwfBM/view"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Malaysia Digital Status certification (opens in new tab)"
              className="inline-flex opacity-80 hover:opacity-100 transition-opacity duration-base ease-out motion-reduce:transition-none"
              data-testid="footer-md-status-badge"
            >
              <img
                src="/brand/md-status-certified.png"
                alt={t("mdStatusAlt")}
                width={112}
                height={55}
                className="h-12 w-auto"
              />
            </a>
            <a
              href="https://drive.google.com/file/d/1EkriAv1SjJjovoLqWaYksADjbNzLoaSQ/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Malaysia Book of Records award (opens in new tab)"
              className="inline-flex opacity-100 hover:opacity-100 transition-opacity duration-base ease-out motion-reduce:transition-none"
              data-testid="footer-mbor-badge"
            >
              <img
                src="/awards/mbor-white.png"
                alt="Malaysia Book of Records award"
                data-emblem="mbor"
                className="h-[150px] w-auto"
                style={{ objectFit: "contain", maxWidth: "calc(100vw - 64px)" }}
              />
            </a>
          </div>
          <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-4">
            <span data-testid="footer-copyright">
              {t("copyright", { year: new Date().getFullYear() })}
            </span>
            <span aria-hidden className="hidden sm:inline text-ink-tertiary">·</span>
            <span>{t("citiesLine")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-caps text-ink-secondary">
        {title}
      </h3>
      <ul className="mt-4 space-y-3">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children, testid }) {
  return (
    <li>
      <Link
        to={to}
        className="text-sm text-ink-secondary hover:text-ink-primary transition-colors duration-fast ease-out"
        data-testid={testid}
      >
        {children}
      </Link>
    </li>
  );
}

function NewsletterForm() {
  const { t } = useT("footer");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState({ status: "idle", error: null });
  const toast = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === "loading") return;
    setState({ status: "loading", error: null });
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, honeypot }),
      });
      if (res.status === 200) {
        setState({ status: "success", error: null });
        toast.success(t("newsletter.success.message"));
        return;
      }
      let message = t("newsletter.errors.generic");
      if (res.status === 400 || res.status === 422)
        message = t("newsletter.errors.invalid");
      else if (res.status === 429)
        message = t("newsletter.errors.rate");
      setState({ status: "error", error: message });
    } catch (err) {
      setState({
        status: "error",
        error: t("newsletter.errors.generic"),
      });
    }
  };

  if (state.status === "success") {
    return (
      <div
        className="mt-6 max-w-md rounded-lg border border-active-border/60 bg-frac-violet/10 p-4"
        data-testid="footer-newsletter-success"
        role="status"
      >
        <div className="text-xs font-semibold uppercase tracking-caps text-eyebrow">
          {t("newsletter.success.eyebrow")}
        </div>
        <p className="mt-2 text-sm text-ink-primary">
          {t("newsletter.success.message")}
        </p>
      </div>
    );
  }

  const hasError = state.status === "error";

  return (
    <form
      className="mt-6 max-w-md"
      onSubmit={submit}
      data-testid="footer-newsletter-form"
      aria-label="Newsletter signup"
      noValidate
    >
      <label
        htmlFor="footer-newsletter-email"
        className="text-xs font-semibold uppercase tracking-caps text-ink-secondary"
      >
        {t("newsletter.label")}
      </label>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1">
          <Input
            id="footer-newsletter-email"
            type="email"
            required
            onDark
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (hasError) setState({ status: "idle", error: null });
            }}
            placeholder={t("newsletter.placeholder")}
            error={hasError ? state.error : null}
            disabled={state.status === "loading"}
            data-testid="footer-newsletter-input"
          />
        </div>
        <Button
          type="submit"
          variant="primary-on-dark"
          size="md"
          loading={state.status === "loading"}
          data-testid="footer-newsletter-submit"
        >
          {t("newsletter.submit")}
          <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </div>

      {/* Off-screen honeypot */}
      <div className="honeypot" aria-hidden="true">
        <label htmlFor="footer-newsletter-hp">Website</label>
        <input
          id="footer-newsletter-hp"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      {!hasError && (
        <p className="mt-3 text-xs text-ink-secondary">
          {t("newsletter.hint")}
        </p>
      )}
    </form>
  );
}

export default SiteFooter;
